import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { smartleadAPI } from '@/lib/smartlead-api'

export interface LinkedInProfile {
  id: string
  sender_name: string
  linkedin_url: string
  daily_connections: number
  daily_inmails: number
  daily_likes: number
  daily_direct_messages: number
  client_id?: string
  client_name?: string
  // Event-based metrics
  connections_sent: number
  connections_accepted: number
  direct_messages_sent: number
  message_replies: number
  inmails_sent: number
  inmail_replies: number
  sync_status: string
  last_synced_at?: string
  needs_sync: boolean
  heyreach_id?: string
  platform_status: string
  created_at: string
  updated_at: string
}

export function useLinkedInProfiles(filterClientId?: string) {
  return useQuery({
    queryKey: ['linkedin_profiles', filterClientId ?? 'all'],
    queryFn: async (): Promise<LinkedInProfile[]> => {
      let query = supabase
        .from('linkedin_profiles')
        .select('*')
        .order('created_at', { ascending: false })

      // Filter by client_id when scoped to a specific client
      if (filterClientId) {
        query = query.eq('client_id', filterClientId)
      }

      const { data, error } = await query
      
      if (error) {
        console.error('Error fetching LinkedIn profiles:', error)
        // Fallback to demo data if table doesn't exist yet
        if (error.code === '42P01') {
          console.log('⚠️ linkedin_profiles table does not exist, returning empty array')
          return []
        }
        throw error
      }
      
      // Transform data to match interface (convert heyreach_id to string if needed)
      return (data || []).map(profile => ({
        ...profile,
        heyreach_id: profile.heyreach_id?.toString() || undefined,
        client_id: profile.client_id || undefined,
        client_name: profile.client_name || undefined,
        // Event-based metrics
        connections_sent: profile.connections_sent || 0,
        connections_accepted: profile.connections_accepted || 0,
        direct_messages_sent: profile.direct_messages_sent || 0,
        message_replies: profile.message_replies || 0,
        inmails_sent: profile.inmails_sent || 0,
        inmail_replies: profile.inmail_replies || 0,
        sync_status: profile.sync_status || 'pending',
        platform_status: profile.platform_status || 'active',
        needs_sync: profile.needs_sync ?? true,
        daily_connections: profile.daily_connections || 40,
        daily_inmails: profile.daily_inmails || 40,
        daily_direct_messages: profile.daily_direct_messages || 40,
        daily_likes: profile.daily_likes || 100,
      })) as LinkedInProfile[]
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useCreateLinkedInProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (profile: Omit<LinkedInProfile, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('linkedin_profiles')
        .insert({
          sender_name: profile.sender_name,
          linkedin_url: profile.linkedin_url,
          daily_connections: profile.daily_connections || 40,
          daily_inmails: profile.daily_inmails || 40,
          daily_likes: profile.daily_likes || 100,
          daily_direct_messages: profile.daily_direct_messages || 40,
          client_id: profile.client_id || null,
          client_name: profile.client_name || null,
          connections_accepted: profile.connections_accepted || 0,
          direct_messages_sent: profile.direct_messages_sent || 0,
          inmails_sent: profile.inmails_sent || 0,
          sync_status: profile.sync_status || 'pending',
          platform_status: profile.platform_status || 'active',
          needs_sync: profile.needs_sync ?? true,
          heyreach_id: profile.heyreach_id ? parseInt(profile.heyreach_id) : null,
          sender_email_address: null, // Can be populated later
        })
        .select()
        .single()
      
      if (error) {
        console.error('Error creating LinkedIn profile:', error)
        throw error
      }
      
      return {
        ...data,
        heyreach_id: data.heyreach_id?.toString() || undefined,
      } as LinkedInProfile
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedin_profiles'] })
      toast.success('LinkedIn profile created successfully')
    },
    onError: (error: any) => {
      console.error('Error creating LinkedIn profile:', error)
      toast.error(error.message || 'Failed to create LinkedIn profile')
    }
  })
}

export function useUpdateLinkedInProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<LinkedInProfile> & { id: string }) => {
      const updateData: any = {
        ...updates,
      }
      
      // Convert heyreach_id to integer if provided
      if (updates.heyreach_id !== undefined) {
        updateData.heyreach_id = updates.heyreach_id ? parseInt(updates.heyreach_id) : null
      }
      
      // Remove fields that shouldn't be updated directly
      delete updateData.id
      delete updateData.created_at
      delete updateData.updated_at
      
      const { data, error } = await supabase
        .from('linkedin_profiles')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()
      
      if (error) {
        console.error('Error updating LinkedIn profile:', error)
        throw error
      }
      
      return {
        ...data,
        heyreach_id: data.heyreach_id?.toString() || undefined,
      } as LinkedInProfile
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedin_profiles'] })
      toast.success('LinkedIn profile updated successfully')
    },
    onError: (error: any) => {
      console.error('Error updating LinkedIn profile:', error)
      toast.error(error.message || 'Failed to update LinkedIn profile')
    }
  })
}

export function useDeleteLinkedInProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('linkedin_profiles')
        .delete()
        .eq('id', id)
      
      if (error) {
        console.error('Error deleting LinkedIn profile:', error)
        throw error
      }
      
      return true
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedin_profiles'] })
      toast.success('LinkedIn profile deleted successfully')
    },
    onError: (error: any) => {
      console.error('Error deleting LinkedIn profile:', error)
      toast.error(error.message || 'Failed to delete LinkedIn profile')
    }
  })
}

export function useSyncLinkedInProfilesFromEvents() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('refresh_all_linkedin_profiles')
      
      if (error) {
        console.error('Error syncing LinkedIn profiles:', error)
        throw error
      }
      
      return data?.[0] || { profiles_updated: 0, profiles_added: 0 }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['linkedin_profiles'] })
      toast.success(
        `Sync completed: ${result.profiles_updated} updated, ${result.profiles_added} added`
      )
    },
    onError: (error: any) => {
      console.error('Error syncing LinkedIn profiles:', error)
      toast.error(error.message || 'Failed to sync LinkedIn profiles')
    }
  })
}